import React, { useMemo, useRef, useState } from 'react';
import { Button, Input, Popconfirm, Select, Space, Tag, Tooltip, message } from 'antd';
import { useLazyQuery, useMutation } from '@apollo/client';
import { AoiBizTagDictItem } from '../graphql/queries/listAoiBizTagDictionary';
import {
    AoiBizTagActionRun,
    LIST_AOI_BIZ_TAG_ACTION_RUNS,
    ListAoiBizTagActionRunsResponse,
} from '../graphql/queries/listAoiBizTagActionRuns';
import {
    AoiBizTagActionMode,
    AoiBizTagActionResult,
    RUN_AOI_BIZ_TAG_ACTION,
    RunAoiBizTagActionResponse,
} from '../graphql/mutations/runAoiBizTagAction';
import { BizTagDraft } from '../types';
import { bizTagAllowedForLevel, bizTagKindLabel, bizTagLevelHint } from '../utils/bizTag';

interface Props {
    mode: 'create' | 'edit';
    /** edit 态必有; create 态为 null (保存后才有 AOI, 标签动作也要等保存) */
    aoiId: number | null;
    zipcode: string | null;
    /** AOI 的 DB 层级 (0 = 1 级 / 1 = 2 级); 用来把不允许挂的标签置灰 */
    aoiDbLevel: number | null;
    dictionary: AoiBizTagDictItem[];
    tags: BizTagDraft[];
    /** 服务端已存的标签 code (detail 带回); 只有已保存的标签才能跑动作 —— 没保存的标签, 服务端还不知道它在这个面上 */
    savedTagCodes: string[];
    readonly?: boolean;
    /** 字典加载失败且没有任何可用项: 显示失败 + 重试 (否则会一直显示"加载中") */
    dictionaryError?: boolean;
    onRetryDictionary?: () => void;
    onChange: (next: BizTagDraft[]) => void;
}

/** 标签 Tag 的颜色 (与列表 chips 一致)。 */
export const BIZ_TAG_COLOR = 'purple';

/**
 * AOI 业务标签编辑器 (落 biz_aoi_attribute.biz_tags, 随衍生属性 upsert 全量覆盖)。
 *
 * <p>字典驱动: 下拉项来自 listAoiBizTagDictionary, 按种类 (事实 / 策略) 分组; 停用的标签与不允许挂在当前层级的
 * 标签置灰并给出原因 (比如 RAW_UNIT_FILL 只允许 2 级小区面) —— 服务端还会再校一遍, 这里只是让运营点之前就知道。
 *
 * <p>每个已选标签一张小卡: 中文名 + 说明 + 备注; 已保存且字典声明了动作的标签, 再带一排动作按钮
 * (预览 / 执行), 由 {@link BizTagActionPanel} 承接。新建态不开放: createBizAoi 不接标签, 保存后再打。
 */
const BizTagEditor: React.FC<Props> = ({
    mode,
    aoiId,
    zipcode,
    aoiDbLevel,
    dictionary,
    tags,
    savedTagCodes,
    readonly = false,
    dictionaryError = false,
    onRetryDictionary,
    onChange,
}) => {
    const dictByCode = useMemo(() => {
        const m = new Map<string, AoiBizTagDictItem>();
        dictionary.forEach((d) => m.set(d.code, d));
        return m;
    }, [dictionary]);

    const selectedCodes = tags.map((t) => t.tag);

    /** 下拉选项按种类分组; 停用 / 层级不符 置灰 (已选中的不置灰, 否则没法取消)。 */
    const groupedOptions = useMemo(() => {
        const groups = new Map<string, AoiBizTagDictItem[]>();
        dictionary.forEach((d) => {
            const list = groups.get(d.kind) ?? [];
            list.push(d);
            groups.set(d.kind, list);
        });
        return Array.from(groups.entries()).map(([kind, items]) => ({
            label: bizTagKindLabel(kind),
            options: items.map((d) => {
                const selected = selectedCodes.includes(d.code);
                const levelOk = bizTagAllowedForLevel(d, aoiDbLevel);
                const levelHint = bizTagLevelHint(d);
                const reasons: string[] = [];
                if (!d.enabled) reasons.push('已停用');
                if (!levelOk && levelHint) reasons.push(levelHint);
                return {
                    value: d.code,
                    label: reasons.length > 0 ? `${d.zhName} (${reasons.join(' / ')})` : d.zhName,
                    disabled: !selected && (!d.enabled || !levelOk),
                };
            }),
        }));
    }, [dictionary, selectedCodes, aoiDbLevel]);

    const handleSelect = (codes: string[]) => {
        // 保留已有草稿 (备注不丢), 新选的补一条空草稿
        const kept = tags.filter((t) => codes.includes(t.tag));
        const added = codes.filter((c) => !tags.some((t) => t.tag === c)).map((c) => ({ tag: c, remark: null }));
        onChange([...kept, ...added]);
    };

    const updateRemark = (code: string, remark: string) => {
        onChange(tags.map((t) => (t.tag === code ? { ...t, remark } : t)));
    };

    const remove = (code: string) => {
        onChange(tags.filter((t) => t.tag !== code));
    };

    return (
        <div>
            <label className="mb-2 block text-sm" style={{ color: 'rgba(0,0,0,.88)' }}>
                业务标签
                <span style={{ color: '#a5adb9', fontWeight: 500 }}>（可多个）</span>
            </label>
            {mode === 'create' ? (
                <div className="text-xs" style={{ color: '#a5adb9' }}>保存 AOI 后可在编辑态打业务标签</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {dictionaryError && dictionary.length === 0 && (
                        <div className="text-xs" style={{ color: '#ff4d4f' }} data-testid="biz-tag-dict-error">
                            业务标签字典加载失败, 暂时不能打标
                            {onRetryDictionary && (
                                <Button size="small" type="link" style={{ padding: '0 0 0 6px' }} onClick={onRetryDictionary}>重试</Button>
                            )}
                        </div>
                    )}
                    <Select
                        mode="multiple"
                        size="small"
                        style={{ width: '100%' }}
                        placeholder={dictionary.length === 0 ? (dictionaryError ? '字典不可用' : '字典加载中…') : '选择业务标签'}
                        value={selectedCodes}
                        options={groupedOptions}
                        disabled={readonly || dictionary.length === 0}
                        onChange={handleSelect}
                        // 下拉里不渲染已选 chips (下面每个标签有自己的卡片), 只显示一个计数占位:
                        // 不能用 tagRender 返回空片段 —— rc-select 的 "+ N ..." 溢出占位不走 tagRender, 会残留一个灰 chip
                        maxTagCount={0}
                        maxTagPlaceholder={(omitted) => `已选 ${omitted.length} 个`}
                    />
                    {tags.map((t) => {
                        const dict = dictByCode.get(t.tag);
                        const saved = savedTagCodes.includes(t.tag);
                        const actions = dict?.actions ?? [];
                        return (
                            <div
                                key={`${aoiId ?? 'new'}-${t.tag}`}
                                data-testid={`biz-tag-card-${t.tag}`}
                                className="rounded-lg p-3"
                                style={{ background: '#fafafa', border: '1px solid #f0f0f0' }}
                            >
                                <div className="flex items-center gap-2">
                                    <Tag className="!mr-0" color={BIZ_TAG_COLOR}>{dict?.zhName ?? t.tag}</Tag>
                                    <span className="text-xs" style={{ color: '#a5adb9' }}>{t.tag}</span>
                                    {!saved && <span className="text-xs" style={{ color: '#d48806' }}>未保存</span>}
                                    <div className="flex-1" />
                                    {!readonly && (
                                        <button
                                            type="button"
                                            title="移除标签"
                                            onClick={() => remove(t.tag)}
                                            style={{
                                                flex: 'none', width: 24, height: 24, border: 'none', borderRadius: 6,
                                                background: 'transparent', color: '#a5adb9', fontSize: 13, cursor: 'pointer',
                                            }}
                                        >✕</button>
                                    )}
                                </div>
                                {dict?.description && (
                                    <div className="mt-1.5 text-xs" style={{ color: 'rgba(0,0,0,.45)', lineHeight: 1.5 }}>
                                        {dict.description}
                                    </div>
                                )}
                                <Input
                                    size="small"
                                    className="mt-2"
                                    placeholder="备注 (工单号 / 原因, 选填)"
                                    value={t.remark ?? ''}
                                    maxLength={255}
                                    disabled={readonly}
                                    onChange={(e) => updateRemark(t.tag, e.target.value)}
                                />
                                {actions.length > 0 && aoiId != null && zipcode != null && (
                                    saved ? (
                                        <BizTagActionPanel
                                            aoiId={aoiId}
                                            zipcode={zipcode}
                                            tag={t.tag}
                                            actions={actions}
                                            readonly={readonly}
                                        />
                                    ) : (
                                        <div className="mt-2 text-xs" style={{ color: '#a5adb9' }}>
                                            保存标签后可执行: {actions.map((a) => a.zhName).join(' / ')}
                                        </div>
                                    )
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default BizTagEditor;

/* ====================================================================== */

interface ActionPanelProps {
    aoiId: number;
    zipcode: string;
    tag: string;
    actions: AoiBizTagDictItem['actions'];
    readonly: boolean;
}

/** 执行历史状态 → 中文。 */
export function runStatusLabel(status: number): string {
    if (status === 3) return '成功';
    if (status === 4) return '失败';
    if (status === 2) return '运行中';
    return '初始化';
}

/** 留痕 message (JSON) → 一行摘要: 删了几条 (历史 ROLLBACK 留痕: 恢复了几条), 失败原因; 解析不了就原样截断。 */
export function describeRun(r: AoiBizTagActionRun): string {
    if (!r.message) return '';
    try {
        const m = JSON.parse(r.message) as { affected?: number; aliasCount?: number; skippedAliasIds?: unknown[]; error?: string };
        const parts: string[] = [];
        if (typeof m.affected === 'number') parts.push(r.mode === 'ROLLBACK' ? `恢复 ${m.affected} 条` : `删除 ${m.affected} 条`);
        if (Array.isArray(m.skippedAliasIds) && m.skippedAliasIds.length > 0) parts.push(`跳过 ${m.skippedAliasIds.length} 条`);
        if (m.error) parts.push(`失败: ${m.error}`);
        return parts.join(', ');
    } catch {
        return r.message.slice(0, 80);
    }
}

/** 动作结果 → 一行可读文案 (后端 message 优先; 没有就按字段拼)。 */
export function describeActionResult(r: AoiBizTagActionResult): string {
    if (r.message) return r.message;
    if (!r.success) return r.errorCode ? `失败 (${r.errorCode})` : '失败';
    if (r.mode === 'PREVIEW') return `候选地址 ${r.candidateAddressCount} 个, 别名 ${r.aliasCount} 条`;
    return r.dryRun ? `试跑: 本应清理 ${r.aliasCount} 条` : `已清理别名 ${r.affectedAliasCount} 条`;
}

/**
 * 单个标签的动作区: 字典里每个动作一组 预览 / 执行 按钮 + 最近执行历史。已删别名不提供恢复 (由地址重新标准化时自动重建)。
 *
 * <p>执行是写操作, 字典 needsConfirm=true 时套 Popconfirm; needsPreview=true 时执行按钮要先预览过一次才亮
 * (预览结果里 truncated 直接把执行置灰 —— 后端也会拒, 这里让运营先看到原因)。
 * 结果 (含业务拒绝的 errorCode / message) 显示在按钮下方, 不弹全局 message 打断操作。
 */
export const BizTagActionPanel: React.FC<ActionPanelProps> = ({ aoiId, zipcode, tag, actions, readonly }) => {
    const [runMutation, { loading: running }] = useMutation<RunAoiBizTagActionResponse>(RUN_AOI_BIZ_TAG_ACTION);
    const [loadRuns, { data: runsData, loading: runsLoading }] = useLazyQuery<ListAoiBizTagActionRunsResponse>(
        LIST_AOI_BIZ_TAG_ACTION_RUNS,
        { fetchPolicy: 'network-only' },
    );
    /** 每个动作最近一次结果 (预览 / 执行共用一格, 最新的覆盖旧的)。 */
    const [lastResult, setLastResult] = useState<Record<string, AoiBizTagActionResult>>({});
    /** 每个动作是否预览过 (needsPreview 的执行门槛)。 */
    const [previewed, setPreviewed] = useState<Record<string, boolean>>({});
    const [historyOpen, setHistoryOpen] = useState<string | null>(null);
    // run() 里 await 之后要读"当前"是否展开着历史, 不能用闭包里 render 时的旧值
    const historyOpenRef = useRef<string | null>(null);
    historyOpenRef.current = historyOpen;

    const run = async (action: string, mode: AoiBizTagActionMode) => {
        try {
            const { data } = await runMutation({
                variables: { input: { aoiId, zipcode, tag, action, mode } },
            });
            const result = data?.runAoiBizTagAction;
            if (!result) {
                message.error('标签动作无响应, 请稍后重试');
                return;
            }
            setLastResult((prev) => ({ ...prev, [action]: result }));
            if (mode === 'PREVIEW' && result.success) {
                setPreviewed((prev) => ({ ...prev, [action]: true }));
            }
            if (mode !== 'PREVIEW' && result.success && !result.dryRun) {
                message.success(result.message ?? '已完成');
                // 执行会新增留痕, 历史打开着就刷新
                if (historyOpenRef.current === action) {
                    void loadRuns({ variables: { aoiId, action, limit: 10 } });
                }
            }
        } catch (err) {
            message.error(`标签动作失败: ${err instanceof Error ? err.message : String(err)}`);
        }
    };

    const toggleHistory = (action: string) => {
        if (historyOpen === action) {
            setHistoryOpen(null);
            return;
        }
        setHistoryOpen(action);
        void loadRuns({ variables: { aoiId, action, limit: 10 } });
    };

    const runs: AoiBizTagActionRun[] = runsData?.listAoiBizTagActionRuns ?? [];

    return (
        <div className="mt-2" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {actions.map((a) => {
                const last = lastResult[a.code];
                const executeBlocked = a.needsPreview && !previewed[a.code];
                const truncated = last?.mode === 'PREVIEW' && last.truncated;
                const executeDisabled = readonly || running || executeBlocked || truncated;
                const executeTip = truncated
                    ? '候选超过上限, 请缩小围栏后再执行'
                    : executeBlocked ? '请先预览' : '';
                const executeButton = (
                    <Button size="small" type="primary" ghost disabled={executeDisabled} loading={running}>
                        执行
                    </Button>
                );
                return (
                    <div key={a.code} data-testid={`biz-tag-action-${a.code}`}>
                        <div className="flex items-center gap-2">
                            <span className="text-xs" style={{ color: 'rgba(0,0,0,.65)' }}>{a.zhName}</span>
                            <div className="flex-1" />
                            <Space size={4}>
                                <Button size="small" loading={running} disabled={running} onClick={() => void run(a.code, 'PREVIEW')}>
                                    预览
                                </Button>
                                {a.needsConfirm ? (
                                    <Popconfirm
                                        title={`确认执行「${a.zhName}」?`}
                                        description="写操作, 删除后不可恢复; 地址下次重新标准化时自动重建别名"
                                        okText="执行"
                                        cancelText="取消"
                                        disabled={executeDisabled}
                                        onConfirm={() => void run(a.code, 'EXECUTE')}
                                    >
                                        <Tooltip title={executeTip}>{executeButton}</Tooltip>
                                    </Popconfirm>
                                ) : (
                                    <Tooltip title={executeTip}>
                                        {React.cloneElement(executeButton, { onClick: () => void run(a.code, 'EXECUTE') })}
                                    </Tooltip>
                                )}
                                <Button size="small" type="link" style={{ padding: 0 }} onClick={() => toggleHistory(a.code)}>
                                    {historyOpen === a.code ? '收起历史' : '历史'}
                                </Button>
                            </Space>
                        </div>
                        {last && (
                            <div
                                className="mt-1 text-xs"
                                data-testid={`biz-tag-action-result-${a.code}`}
                                style={{ color: last.success ? (last.dryRun ? '#d48806' : 'rgba(0,0,0,.65)') : '#ff4d4f', lineHeight: 1.5 }}
                            >
                                {last.mode === 'PREVIEW' ? '预览: ' : '执行: '}
                                {describeActionResult(last)}
                            </div>
                        )}
                        {historyOpen === a.code && (
                            <div className="mt-1" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {runsLoading && <span className="text-xs" style={{ color: '#a5adb9' }}>加载中…</span>}
                                {!runsLoading && runs.length === 0 && (
                                    <span className="text-xs" style={{ color: '#a5adb9' }}>暂无执行记录</span>
                                )}
                                {runs.map((r) => {
                                    const summary = describeRun(r);
                                    return (
                                        <div key={String(r.taskId)} className="flex items-center gap-2 text-xs" style={{ color: 'rgba(0,0,0,.65)' }}>
                                            <span>#{String(r.taskId)}</span>
                                            <span>{r.mode === 'ROLLBACK' ? '回滚' : '执行'}</span>
                                            <span style={{ color: r.status === 4 ? '#ff4d4f' : undefined }}>{runStatusLabel(r.status)}</span>
                                            <span style={{ color: '#a5adb9' }}>{(r.endedAt ?? r.startedAt ?? '').replace('T', ' ').slice(0, 16)}</span>
                                            {summary && <span style={{ color: r.status === 4 ? '#ff4d4f' : '#a5adb9' }}>{summary}</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
